import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  DIFFUSION_TYPES,
  VIDEO_PLATFORMS,
  BLOG_PLATFORMS,
  DIFFUSION_TYPE_LABELS,
  DIFFUSION_PLATFORM_LABELS,
  DIFFUSION_STATUS_LABELS,
  type DiffusionStatus,
} from '@jeraisonne/db/diffusions';
import type { DiffusionsFilters } from '@/lib/admin/diffusions-queries';

const STATUSES: DiffusionStatus[] = ['draft', 'scheduled', 'publishing', 'published', 'failed'];
const ALL_PLATFORMS = [...VIDEO_PLATFORMS, ...BLOG_PLATFORMS];

const selectCls =
  'border-ink-200 focus:border-primary-500 focus:ring-primary-500/30 w-full rounded-xl border bg-white px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2';

interface FilterBarProps {
  filters: DiffusionsFilters;
  onChange: (next: DiffusionsFilters) => void;
}

export function DiffusionsFilterBar({ filters, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const active = !!(filters.type || filters.platform || filters.status);

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'border-ink-200 hover:bg-ink-50 inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 font-sans text-[13px] font-semibold transition-colors',
            active && 'border-primary-300 text-primary-700',
          )}
        >
          <Filter className="h-3.5 w-3.5" />
          Filtrer
          {active && (
            <span className="bg-primary-500 ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white">
              {[filters.type, filters.platform, filters.status].filter(Boolean).length}
            </span>
          )}
        </button>
        {active && (
          <button
            onClick={() => onChange({})}
            className="text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 font-sans text-[13px]"
          >
            <X className="h-3.5 w-3.5" /> Effacer
          </button>
        )}
      </div>

      {open && (
        <div className="border-ink-200 mt-3 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-4 sm:grid-cols-3">
          <div>
            <label className="font-display text-ink-600 mb-1 block text-[11px] font-bold uppercase tracking-wide">
              Type
            </label>
            <select
              value={filters.type ?? ''}
              onChange={(e) =>
                onChange({ ...filters, type: (e.target.value || '') as DiffusionsFilters['type'] })
              }
              className={selectCls}
            >
              <option value="">Tous les types</option>
              {DIFFUSION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DIFFUSION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-display text-ink-600 mb-1 block text-[11px] font-bold uppercase tracking-wide">
              Réseau
            </label>
            <select
              value={filters.platform ?? ''}
              onChange={(e) =>
                onChange({
                  ...filters,
                  platform: (e.target.value || '') as DiffusionsFilters['platform'],
                })
              }
              className={selectCls}
            >
              <option value="">Tous les réseaux</option>
              {ALL_PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {DIFFUSION_PLATFORM_LABELS[p]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-display text-ink-600 mb-1 block text-[11px] font-bold uppercase tracking-wide">
              Statut
            </label>
            <select
              value={filters.status ?? ''}
              onChange={(e) =>
                onChange({
                  ...filters,
                  status: (e.target.value || '') as DiffusionsFilters['status'],
                })
              }
              className={selectCls}
            >
              <option value="">Tous les statuts</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {DIFFUSION_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
